import type { FC } from 'react';
import AirportIcon from '@this/components/shared/icons/airport_icon';
import BulletTrainIcon from '@this/components/shared/icons/bullet_train';
import TripsHotelIcon from '@this/components/shared/icons/trips_hotel_icon';
import TripsCarIcon from '@this/components/shared/icons/trips_car_icon';
import StationIcon from '@this/components/shared/icons/station_icon';
import ForeignAirTicket from '@this/components/shared/icons/foreign_air_ticket';
import AirTicketPackage from '@this/components/shared/icons/air_ticket_package';
import type { ForbiddenRepositoryArgs } from '@this/domain/organization/organization3';
import type {
  ArrangementRequestLimitSummary,
  LimitTypeSummary
} from '@this/domain/organization/arrangement_request_limit_summary';

type ForbiddenMapping = { category: string; repositories: string[] };

export type CategoryConfig = {
  id: string;
  label: string;
  icon: FC;
  iconBaseSize?: number;
  forbidden: ForbiddenMapping[];
  getLimit: (summary: ArrangementRequestLimitSummary) => LimitTypeSummary | null;
  deadlineText: (limit: LimitTypeSummary | null, premiumSupport: boolean) => string;
  notes: (limit: LimitTypeSummary | null, premiumSupport: boolean) => string[];
};

export const UNAVAILABLE_NOTE =
  '貴社の設定では利用できません。\n利用を希望される場合は、社内管理者にお問い合わせください。';

export const deadlineLabel = (businessDays: number, premiumSupport: boolean): string => {
  if (businessDays <= 1) return '前日';
  return premiumSupport ? `${businessDays}日前` : `${businessDays}営業日前`;
};

const formatHour = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

const deadlineSentence = (baseDate: string, limit: LimitTypeSummary, premiumSupport: boolean): string =>
  `${baseDate}${deadlineLabel(limit.businessDays, premiumSupport)} ${formatHour(limit.hour)} まで`;

const departureDeadline =
  (baseDate: string) =>
  (limit: LimitTypeSummary | null, premiumSupport: boolean): string =>
    limit ? deadlineSentence(baseDate, limit, premiumSupport) : UNAVAILABLE_NOTE;

export const CATEGORY_CONFIGS: CategoryConfig[] = [
  {
    id: 'domesticAir',
    label: '国内航空券',
    icon: AirportIcon,
    iconBaseSize: 16,
    forbidden: [{ category: 'domestic_flight', repositories: ['sakura'] }],
    getLimit: summary => summary.domesticAir,
    deadlineText: departureDeadline('搭乗日の'),
    notes: () => []
  },
  {
    id: 'shinkansen',
    label: '新幹線',
    icon: BulletTrainIcon,
    forbidden: [{ category: 'domestic_railway', repositories: ['shinkansen'] }],
    getLimit: summary => summary.shinkansen,
    deadlineText: departureDeadline('乗車日の'),
    notes: () => []
  },
  {
    id: 'hotel',
    label: '宿泊',
    icon: TripsHotelIcon,
    forbidden: [
      {
        category: 'domestic_hotel',
        repositories: ['ean_rapid', 'rakuten_hotel', 'rakuten_static_file_hotel', 'agoda', 'hotel_skip']
      },
      {
        category: 'foreign_hotel',
        repositories: ['ean_rapid', 'agoda', 'hotel_skip']
      }
    ],
    getLimit: summary => summary.hotel,
    deadlineText: () => 'チェックイン当日、宿泊施設の最終受付時刻まで',
    notes: (limit, premiumSupport) =>
      limit ? [`※自動予約対象外施設は、${deadlineSentence('チェックインの', limit, premiumSupport)}。`] : []
  },
  {
    id: 'foreignAir',
    label: '海外航空券',
    icon: ForeignAirTicket,
    forbidden: [{ category: 'foreign_flight', repositories: ['amadeus', 'travelport'] }],
    getLimit: () => null,
    deadlineText: () => '便・運賃により依頼期限が異なります。',
    notes: () => ['※現在、土日祝日はご依頼いただけません。']
  },
  {
    id: 'package',
    label: '航空券パッケージ',
    icon: AirTicketPackage,
    forbidden: [{ category: 'domestic_flight_package', repositories: ['rakuten_package'] }],
    getLimit: summary => summary.package,
    deadlineText: departureDeadline('搭乗日の'),
    notes: () => ['航空券＋宿泊セット。出発8日以上前だとチケットが割安になる傾向があります。']
  },
  {
    id: 'railwayPackage',
    label: '鉄道パッケージ',
    icon: StationIcon,
    iconBaseSize: 16,
    forbidden: [{ category: 'domestic_railway_package', repositories: ['jr_package'] }],
    getLimit: summary => summary.railwayPackage,
    deadlineText: departureDeadline('乗車日の'),
    notes: () => ['JR東日本エリアのみ対象です。']
  },
  {
    id: 'rentalCar',
    label: 'レンタカー',
    icon: TripsCarIcon,
    forbidden: [{ category: 'rentalcar', repositories: ['smas'] }],
    getLimit: summary => summary.rentalCar,
    deadlineText: departureDeadline('利用開始日の'),
    notes: () => []
  }
];

export const isCategoryAvailable = (
  forbidden: ForbiddenMapping[],
  forbiddenRepositories: ForbiddenRepositoryArgs[]
): boolean =>
  forbidden.some(({ category, repositories }) =>
    repositories.some(repo => !forbiddenRepositories.some(f => f.category === category && f.repository === repo))
  );

export const badgeText = (summary: ArrangementRequestLimitSummary): string =>
  summary.premiumSupport ? '土日祝日オプション適用中' : '土日祝日オプション適用外';
